<template>
  <FiltersItem>
    <template #title>
      {{ $l('showresult.filter.priceTitle') }}
    </template>
    <template #content>
      <!-- Price range -->
      <FiltersItemPriceRange v-if="resultsQueryStore?.state?.response?.uniquePrices?.length" />

      <!-- Products -->
      <FiltersItemContent v-if="products.length > 1">
        <template #title>
          {{ $l('showresult.filter.includedPrice.title') }}
        </template>
        <template #content>
          <FiltersList
            :items="products"
            class="filtersList--price"
            :show-exclusive="false"
            @itemClicked="productToggle"
          >
            <template #textEnd="{item}">
              <template
                v-if="item.key === 'climateCompensated'"
              >
                <img
                  src="/assets/images/global/climateComp-icon.png"
                  loading="lazy"
                >
              </template>
            </template>
          </FiltersList>
        </template>
      </FiltersItemContent>

      <!-- Payment method -->
      <FiltersItemPaymentMethod
        v-if="resultsQueryStore?.state?.response?.uniquePaymentMethods?.length"
      />
    </template>

    <template #summary>
      <FiltersItemSummary :items="summaryItems" />
    </template>
  </FiltersItem>
</template>

<script setup>
import { l, lc, lreplace } from '@utils/toolbox';
import { Base64 } from '@js/plugins/base64';

import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

const filtersStore = useFiltersStore();
const resultsQueryStore = useResultQueryStore();

const summaryItems = computed(() => {
	const summary = [];

	// Price
	if (filtersStore.state.price) {
		const price = lreplace('showresult.filter.summary.totalPrice', { lc: lc(filtersStore.state.price) });
		summary.push(price);
	}

	if (filtersStore.state.flexTicket) {
		summary.push(lreplace('showresult.filter.summary.flexTicket', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.baggageIncluded) {
		summary.push(lreplace('showresult.filter.summary.baggageIncluded', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.youthTicket) {
		summary.push(lreplace('showresult.filter.summary.youthTicket', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.baggageIncluded) {
		summary.push(lreplace('showresult.filter.summary.baggageIncluded', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.cabinBaggageIncluded) {
		summary.push(lreplace('showresult.filter.summary.cabinBaggageIncluded', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.rebookableFlights) {
		summary.push(lreplace('showresult.filter.summary.rebookableIncluded', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.climateCompensated) {
		summary.push(lreplace('showresult.filter.summary.carbonOffsetIncluded', { included: '<span>' + l('global.simplePhrases.included') + '</span>' }));
	}

	if (filtersStore.state.paymentMethodIncluded) {
		const cardType = js_params.module.Filters.paymentMethodsAll[filtersStore.state.paymentMethodIncluded];
		summary.push(lreplace('showresult.filter.summary.paymentWith', { card: cardType }));
	}


	return summary;
});

const products = computed(() => {
	const items = [
		{
			text: l('showresult.filter.flexTicket'),
			key: 'flexTicket',
			value: 1,
			tooltipText: 'showresult.filter.flexTicketToolTip',
			tooltipIcon: 'info',
			isChecked: filtersStore.state.flexTicket,
			enabledValue: true
		},
		{
			text: l('global.simplePhrases.baggageIncluded'),
			key: 'baggageIncluded',
			value: 1,
			tooltipText: 'searchBox.baggage.restriction',
			tooltipIcon: 'info',
			isChecked: filtersStore.state.baggageIncluded,
			enabledValue: js_params.passengers - js_params.infants
		},
		{
			text: l('global.simplePhrases.cabinBaggageIncluded'),
			value: 1,
			key: 'cabinBaggageIncluded',
			tooltipText: 'searchBox.cabinBaggage.restriction',
			tooltipIcon: 'info',
			isChecked: filtersStore.state.cabinBaggageIncluded,
			enabledValue: js_params.passengers - js_params.infants
		},
		{
			text: l('showresult.filter.youthTicket'),
			value: 1,
			key: 'youthTicket',
			tooltipText: 'showresult.filter.youthTicket.tooltip',
			tooltipIcon: 'info',
			isChecked: filtersStore.state.youthTicket,
			enabledValue: true,
			disabled: !resultsQueryStore.state.response?.meta?.hasYouthTickets
		},
		{
			text: l('showresult.filter.showClimateCompensationDesktop'),
			value: 1,
			key: 'climateCompensated',
			tooltipText: 'showresult.climateComp.popup',
			tooltipIcon: 'info',
			tooltipReplace: Base64.encode(JSON.stringify({ informationPage: l('global.navigation.informationLink') })),
			isChecked: filtersStore.state.climateCompensated,
			enabledValue: true
		}
	];

	// If youth tickets are not available, remove the youth ticket option
	return js_params?.isYouthSearch ? items : items.filter(item => item.key !== 'youthTicket');
});

async function productToggle (event) {
	const { key } = event;
	const item = products.value.find(i => i.key === key);
	const value = filtersStore.state[key] ? null : item.enabledValue;
	await filtersStore.set({ key, value });
}
</script>

<style lang="scss" scoped>
img {
	display: none;
	margin-left: 0.2rem;
	aspect-ratio: 1/1;

	@include breakpoint_up(md) {
		height: 3.2rem;
		margin-top: 0.1rem;
		display: block;
	}
}

@include breakpoint_up(md) {
	:deep(.filtersList .checkBoxContainer) {
		padding-left: 2rem;
		padding-right: 1rem;

		.filterInfo {
			left: -.5rem;
			top: -.3rem;
		}
	}
}
</style>
